import { Form } from 'antd';
import styled from 'styled-components';

import { StyledInput, SelectInput } from 'components';
import { FormContentStyles } from 'theme';
import { BESTFIT_NONE } from 'const';

const Wrapper = styled.div<{ visible: boolean }>`
  display: ${(props) => (props.visible ? 'block' : 'none')};
  ${FormContentStyles}
`;

const INPUT_SPACING = '43px';

const StyledFormItem = styled(Form.Item)`
  margin: 0;
`;

const Row = styled.div`
  margin-top: ${INPUT_SPACING};
`;

interface OnboardingQuestionsProps {
  visible: boolean;
  hasMembershipSite: boolean;
}

export const QUESTION_NAMES = ['BestFit', 'DataSource', 'MemberCount', 'HeardAboutUs'];
export const ALTERNATE_QUESTION_NAMES = ['BestFit', 'SocialHandle', 'FollowerCount', 'HeardAboutUs'];

export const OnboardingQuestions = ({ visible, hasMembershipSite }: OnboardingQuestionsProps) => {
  const form = Form.useFormInstance();

  return (
    <Wrapper visible={visible}>
      <StyledFormItem name="BestFit">
        <SelectInput
          optionLabelProp="label" // Show the tag values with this prop
          showArrow
          mode="multiple"
          options={[
            { name: "I'm setting up my online courses/membership", value: 'Setting Up', label: 'Setting Up' },
            { name: 'I sell online courses', value: 'Online Courses', label: 'Online Courses' },
            { name: 'I have a membership site', value: 'Membership', label: 'Membership' },
            { name: 'I have an OTT subscription channel', value: 'OTT Subscription', label: 'OTT Subscription' },
            { name: 'None of the above', value: BESTFIT_NONE, label: 'None of the above' },
          ]}
          placeholder="Which of these best fit you?"
          size="large"
          onSelect={(a: unknown) => {
            const current = (form.getFieldValue('BestFit') ?? []) as string[];
            if ((a as string) === BESTFIT_NONE) {
              // If None of the above is selected, clear other selection
              form.setFieldValue('BestFit', [BESTFIT_NONE]);
            } else {
              // For any other selection, ensure None of the above is not still selected
              if (current.includes(BESTFIT_NONE)) {
                form.setFieldValue(
                  'BestFit',
                  current.filter((v) => v !== BESTFIT_NONE),
                );
              }
            }
          }}
        />
      </StyledFormItem>

      {hasMembershipSite ? (
        <>
          <Row>
            <StyledFormItem name="DataSource">
              <SelectInput
                options={[
                  { name: 'Kajabi', value: 'Kajabi' },
                  { name: 'Uscreen', value: 'Uscreen' },
                  { name: 'Vimeo OTT', value: 'VHX' },
                  { name: 'WordPress', value: 'WordPress' },
                  { name: 'Other', value: 'Other' },
                ]}
                placeholder="What is your membership site built with?"
                size="large"
              />
            </StyledFormItem>
          </Row>
          <Row>
            <StyledFormItem name="MemberCount">
              <SelectInput
                options={[
                  { name: '0 - 200', value: '0 - 200' },
                  { name: '200 - 500', value: '200 - 500' },
                  { name: '500 - 1,000', value: '500 - 1,000' },
                  { name: '1,000 - 3,000', value: '1,000 - 3,000' },
                  { name: '3,000+', value: '3,000+' },
                ]}
                placeholder="How many paying members do you have?"
                size="large"
              />
            </StyledFormItem>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <StyledFormItem name="SocialHandle">
              <StyledInput placeholder="What is your social media handle?" />
            </StyledFormItem>
          </Row>
          <Row>
            <StyledFormItem name="FollowerCount">
              <SelectInput
                options={[
                  { name: '0 - 5K', value: '0 - 5K' },
                  { name: '5K - 15K', value: '5K - 15K' },
                  { name: '15K - 50K', value: '15K - 50K' },
                  { name: '50K - 200K', value: '50K - 200K' },
                  { name: '200K - 1M', value: '200K - 1M' },
                  { name: '1M +', value: '1M +' },
                ]}
                placeholder="How many followers do you have?"
                size="large"
              />
            </StyledFormItem>
          </Row>
        </>
      )}
      <Row>
        <StyledFormItem name="HeardAboutUs">
          <SelectInput
            options={[
              { name: 'Someone I know recommended VidApp', value: 'Someone I know' },
              { name: "I saw another app you've developed", value: 'Another app by VidApp' },
              { name: 'I searched Google organically', value: 'Google organically' },
              { name: 'I clicked on a Google Ad', value: 'Google Ad' },
              { name: 'I clicked on a Facebook/Instagram Ad', value: 'Facebook/Instagram Ad' },
              { name: 'Other', value: 'Other' },
            ]}
            placeholder="How did you hear about us?"
            size="large"
          />
        </StyledFormItem>
      </Row>
    </Wrapper>
  );
};
